import {
    GovFormControl,
    GovFormInput,
    GovFormLabel,
    GovFormSelect,
    GovSkeleton,
    GovSpacer
} from "@gov-design-system-ce/react";
import {useEffect, useMemo, useState} from "react";
import {getMonthsInRange, mapDateFormat, mapFinancingType, parseJwt} from "../utils/utils";
import CzechRepublicProjectsMap from "../dashboardComponents/CzechRepublicProjectsMap";
import "../css/dashboard-projects.css";

function getMonthString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
}

function getCountForFinancingType(row, financingTypeId) {
    if (!row) {
        return 0;
    }
    if (financingTypeId === "all" || financingTypeId === "" || financingTypeId == null) {
        return row.totalCount || 0;
    }
    const match = (row.byFinancingType || []).find(
        (item) => String(item.financingType?.id) === String(financingTypeId)
    );
    return match ? (match.count || 0) : 0;
}

function financingTypeLabel(financingType) {
    if (!financingType) {
        return "Neznámý typ financování";
    }
    const mapped = mapFinancingType(String(financingType.id));
    if (mapped !== "Neznámý typ financování") {
        return mapped;
    }
    if (financingType.name === "NOT_SELECTED") {
        return mapFinancingType("0");
    }
    return financingType.name || mapped;
}

function buildMonthlyMatrix(monthlyTimeline, summaryRows, rowKey, idField, financingTypeId) {
    const matrix = {};
    const rowOrder = [];
    const rowNames = {};

    (summaryRows || []).forEach((row) => {
        const entity = row[idField];
        if (!entity) {
            return;
        }
        const id = entity.id;
        if (!matrix[id]) {
            matrix[id] = {};
            rowOrder.push(id);
            rowNames[id] = entity.name;
        }
    });

    (monthlyTimeline || []).forEach((monthEntry) => {
        const yearMonth = monthEntry.yearMonth;
        const rows = (monthEntry.data && monthEntry.data[rowKey]) || [];
        rows.forEach((row) => {
            const entity = row[idField];
            if (!entity) {
                return;
            }
            const id = entity.id;
            if (!matrix[id]) {
                matrix[id] = {};
                rowOrder.push(id);
                rowNames[id] = entity.name;
            }
            matrix[id][yearMonth] = getCountForFinancingType(row, financingTypeId);
        });
    });

    return {matrix, rowOrder, rowNames};
}

function getCount(matrix, rowId, yearMonth) {
    if (!matrix[rowId]) {
        return 0;
    }
    return matrix[rowId][yearMonth] || 0;
}

function getRowSum(matrix, rowId, months) {
    return months.reduce((total, yearMonth) => total + getCount(matrix, rowId, yearMonth), 0);
}

function getMonthSum(matrix, rowIds, yearMonth) {
    return rowIds.reduce((total, rowId) => total + getCount(matrix, rowId, yearMonth), 0);
}

function ProjectsCountsTable({title, firstColumnLabel, rowIds, rowNames, matrix, months}) {
    if (!rowIds.length) {
        return (
            <div>
                <div className="dashboard-projects-section-title">{title}</div>
                <p>Pro zvolené období nejsou k dispozici žádná data.</p>
            </div>
        );
    }

    return (
        <div>
            <div className="dashboard-projects-section-title">{title}</div>
            <div className="table-consultations411-counts-container">
                <table cellSpacing="0" cellPadding="0" className="table-consultations411-counts">
                    <thead>
                    <tr>
                        <th>{firstColumnLabel}</th>
                        {months.map((yearMonth) => (
                            <th key={yearMonth}>{mapDateFormat(yearMonth)}</th>
                        ))}
                        <th>Celkem</th>
                    </tr>
                    </thead>
                    <tbody>
                    {rowIds.map((rowId) => (
                        <tr key={rowId}>
                            <td className="first">{rowNames[rowId]}</td>
                            {months.map((yearMonth) => (
                                <td key={yearMonth}>{getCount(matrix, rowId, yearMonth)}</td>
                            ))}
                            <td><strong>{getRowSum(matrix, rowId, months)}</strong></td>
                        </tr>
                    ))}
                    <tr>
                        <td className="first"><strong>Celkem</strong></td>
                        {months.map((yearMonth) => (
                            <td key={yearMonth}>
                                <strong>{getMonthSum(matrix, rowIds, yearMonth)}</strong>
                            </td>
                        ))}
                        <td>
                            <strong>
                                {rowIds.reduce((total, rowId) => total + getRowSum(matrix, rowId, months), 0)}
                            </strong>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export function DashboardProjectsContent() {
    const authority = parseJwt(localStorage.getItem("jwt")).role[0].authority;
    const RCid = localStorage.getItem("RCid");
    const isHeadquarters = authority === "HEADQUARTERS" || authority === "HQ_WITH_REPORT";
    const jwt = localStorage.getItem("jwt");

    const currentDate = new Date();
    const pastDate = new Date(2024, 0);

    const [regionalCenter, setRegionalCenter] = useState(isHeadquarters ? "0" : RCid);
    const [financingType, setFinancingType] = useState("all");
    const [startMonth, setStartMonth] = useState(getMonthString(pastDate));
    const [endMonth, setEndMonth] = useState(getMonthString(currentDate));
    const [data, setData] = useState(null);
    const [dataIsLoading, setDataIsLoading] = useState(true);

    const months = useMemo(() => getMonthsInRange(startMonth, endMonth), [startMonth, endMonth]);

    const financingTypeOptions = useMemo(() => {
        const knownIds = [0, 1, 2, 13, 14, 10, 3, 8, 9, 4, 11, 12, 5, 6, 7];
        const fromSummary = {};
        (data?.summary?.byFinancingType || []).forEach((item) => {
            if (item.financingType?.id != null) {
                fromSummary[item.financingType.id] = item.financingType;
            }
        });
        return knownIds.map((id) => fromSummary[id] || {id, name: mapFinancingType(String(id))});
    }, [data]);

    const regionMatrix = useMemo(
        () => buildMonthlyMatrix(
            data?.monthlyTimeline,
            data?.summary?.byRegion,
            "byRegion",
            "region",
            financingType
        ),
        [data, financingType]
    );
    const rcMatrix = useMemo(
        () => buildMonthlyMatrix(
            data?.monthlyTimeline,
            data?.summary?.byRc,
            "byRc",
            "rc",
            financingType
        ),
        [data, financingType]
    );

    const regionCountsForMap = useMemo(() => {
        if (data?.summary?.byRegion) {
            return data.summary.byRegion.map((row) => ({
                region: row.region,
                totalCount: getCountForFinancingType(row, financingType)
            }));
        }
        return regionMatrix.rowOrder.map((id) => ({
            region: {id, name: regionMatrix.rowNames[id]},
            totalCount: getRowSum(regionMatrix.matrix, id, months)
        }));
    }, [data, regionMatrix, months, financingType]);

    useEffect(() => {
        setDataIsLoading(true);
        setData(null);

        let url = process.env.REACT_APP_API_URL + `/dashboard/projects?from=${startMonth}&to=${endMonth}`;
        if (regionalCenter && regionalCenter !== "0") {
            url += `&rcId=${regionalCenter}`;
        }

        fetch(url, {
            headers: {
                'Authorization': `Bearer ${jwt}`
            }
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then((jsonData) => {
                setData(jsonData);
                setDataIsLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching projects dashboard data:', error);
                setDataIsLoading(false);
            });
    }, [startMonth, endMonth, regionalCenter, jwt]);

    return (
        <div>
            <div className="gov-container dashboard-container padding-top-0">
                <div className="gov-container__content">
                    <div className="gov-title--beta">
                        Dashboard projektů{!isHeadquarters ? `: ${localStorage.getItem("RCname") || ""}` : ""}
                    </div>
                    <div className="gov-flexbox gov-flexbox--justify-start flex-wrap">
                        {isHeadquarters &&
                            <GovFormControl>
                                <GovFormSelect size="s" placeholder="Regionální centrum"
                                               onGov-change={(event) => setRegionalCenter(event.target.value)}
                                               value={regionalCenter}>
                                    <option disabled="disabled" value="">Vyberte regionální centrum</option>
                                    <option value="0">Všechna regionální centra</option>
                                    <option value="1">Moravskoslezský kraj</option>
                                    <option value="2">Středočeský kraj a Praha</option>
                                    <option value="3">Ústecký a Karlovarský kraj</option>
                                    <option value="4">Královehradecký a Liberecký kraj</option>
                                    <option value="5">Pardubický kraj a kraj Vysočina</option>
                                    <option value="6">Jihočeský kraj a Plzeňský kraj</option>
                                    <option value="7">Zlínský a Jihomoravský kraj</option>
                                    <option value="8">Olomoucký kraj</option>
                                </GovFormSelect>
                                <GovFormLabel slot="top" size="s">Regionální centrum</GovFormLabel>
                            </GovFormControl>
                        }
                        <GovFormControl className="ml-5">
                            <GovFormSelect size="s" placeholder="Typ financování"
                                           onGov-change={(event) => setFinancingType(event.target.value)}
                                           value={financingType}>
                                <option value="all">Všechny typy financování</option>
                                {financingTypeOptions.map((ft) => (
                                    <option key={ft.id} value={String(ft.id)}>
                                        {financingTypeLabel(ft)}
                                    </option>
                                ))}
                            </GovFormSelect>
                            <GovFormLabel slot="top" size="s">Typ financování</GovFormLabel>
                        </GovFormControl>
                        <GovFormControl className="ml-5">
                            <GovFormInput value={startMonth}
                                          onGov-input={(event) => setStartMonth(event.target.value)}
                                          size="s"
                                          variant="secondary" input-type="month"></GovFormInput>
                            <GovFormLabel slot="top" size="s">Měsíc od:</GovFormLabel>
                        </GovFormControl>
                        <GovFormControl className="ml-5">
                            <GovFormInput value={endMonth}
                                          onGov-input={(event) => setEndMonth(event.target.value)}
                                          size="s"
                                          variant="secondary" input-type="month"></GovFormInput>
                            <GovFormLabel slot="top" size="s">Měsíc do:</GovFormLabel>
                        </GovFormControl>
                    </div>
                    <GovSpacer size="m"></GovSpacer>

                    {!dataIsLoading && data ?
                        <div>
                            <ProjectsCountsTable
                                title="Počet projektů dle krajů"
                                firstColumnLabel="Kraj"
                                rowIds={regionMatrix.rowOrder}
                                rowNames={regionMatrix.rowNames}
                                matrix={regionMatrix.matrix}
                                months={months}
                            />
                            <GovSpacer size="l"></GovSpacer>
                            <ProjectsCountsTable
                                title="Počet projektů dle regionálních center"
                                firstColumnLabel="Regionální centrum"
                                rowIds={rcMatrix.rowOrder}
                                rowNames={rcMatrix.rowNames}
                                matrix={rcMatrix.matrix}
                                months={months}
                            />
                            <GovSpacer size="l"></GovSpacer>
                            <CzechRepublicProjectsMap
                                regionCounts={regionCountsForMap}
                                title="Mapa počtu projektů dle krajů"
                            />
                        </div>
                        :
                        <GovSkeleton wcag-label="Loading content of page" variant="secondary"
                                     shape="text" animation="progress" count="1"
                                     height="700px"></GovSkeleton>
                    }
                </div>
            </div>
        </div>
    );
}
